import { LoadDisposition } from '@contracts/order/valueNormalizers/LoadDisposition';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { FormTextInput } from '@src/common/components/FormTextInput';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { FormMultipleOptions } from '@src/orders/components/FormMultipleOptions';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { BaseInputType, FormEntries } from '@src/orders/FormEntries';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { FormDimensionInput } from '@src/orders/PermitFormSections/FormDimensionInput';
import { FormNumberInput } from '@src/orders/PermitFormSections/FormNumberInput';
import { FormWeightInput } from '@src/orders/PermitFormSections/FormWeightInput';
import { loadinformationSectionFields } from '@src/orders/PermitFormSections/loadInformation/LoadinformationSectionFields';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  CommodityField,
  FloridaLoadDescriptionField,
  FloridaLoadIdentityField,
  FloridaPermitTypeField,
  LoadDescriptionField,
  LoadDispositionField,
  LoadEndToEndCountField,
  LoadHeightField,
  LoadIndustryTypeField,
  LoadIsNewlyManufacturedField,
  LoadIsSingleItemField,
  LoadLengthField,
  LoadLoadingMethod,
  LoadMakeField,
  LoadModelField,
  LoadOwnerAddressField,
  LoadOwnerNameField,
  LoadQuantityField,
  LoadSealedContainerChassisNumberField,
  LoadSealedContainerNumberField,
  LoadSerialNumberField,
  LoadSideBySideCountField,
  LoadStackCountField,
  LoadWeightField,
  LoadWidthField,
  LoadYearField,
} from '@src/permitField/LoadInformationFields';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = { fields: string[] } & CollapseListener;

export const LoadInformationSection = (props: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();

  const {
    register,
    control,
    formState: { errors, disabled: formDisabled },
    setValue,
    unregister,
  } = useFormContext<FormEntries<typeof props.fields>>();

  const [loadStackCount] = useWatch({
    control,
    name: [LoadStackCountField.fieldName],
  });

  const [loadEndToEndCount] = useWatch({
    control,
    name: [LoadEndToEndCountField.fieldName],
  });

  const [loadSideBySideCount] = useWatch({
    control,
    name: [LoadSideBySideCountField.fieldName],
  });

  const loadDispositionValue: BaseInputType = useWatch({
    control,
    name: LoadDispositionField.fieldName,
  });

  const loadIsSingleItemValue = useWatch({
    control,
    name: LoadIsSingleItemField.fieldName,
  });

  const isSingleItemValue = useMemo((): boolean => {
    if (loadIsSingleItemValue !== undefined) {
      return loadIsSingleItemValue as boolean;
    }
    const cannotAddLoadDisposition =
      loadDispositionValue === undefined &&
      isFieldDisabled(LoadDispositionField.fieldName);
    return cannotAddLoadDisposition;
  }, [loadIsSingleItemValue, loadDispositionValue, isFieldDisabled]);

  useEffect(() => {
    if (
      loadStackCount &&
      !hasLoadDisposition(loadDispositionValue, LoadDisposition.loadIsStacked)
    ) {
      unregister(LoadStackCountField.fieldName);
    }
    if (
      loadEndToEndCount &&
      !hasLoadDisposition(loadDispositionValue, LoadDisposition.loadIsEndToEnd)
    ) {
      unregister(LoadEndToEndCountField.fieldName);
    }
    if (
      loadSideBySideCount &&
      !hasLoadDisposition(
        loadDispositionValue,
        LoadDisposition.loadIsSideBySide,
      )
    ) {
      unregister(LoadSideBySideCountField.fieldName);
    }
  }, [
    loadDispositionValue,
    loadEndToEndCount,
    loadSideBySideCount,
    loadStackCount,
    unregister,
  ]);

  const { sectionInError } = useSectionError(loadinformationSectionFields);

  const isLoadIsSingleItemDisabled = useMemo((): boolean => {
    const cannotChangeLoadDisposition =
      loadDispositionValue !== undefined &&
      isFieldDisabled(LoadDispositionField.fieldName);
    if (cannotChangeLoadDisposition) {
      return true;
    }

    return isFieldDisabled(LoadIsSingleItemField.fieldName);
  }, [loadDispositionValue, isFieldDisabled]);

  const isLoadDispositionDisabled = useMemo((): boolean => {
    const cannotChangeToMultipleItems =
      loadIsSingleItemValue === true &&
      isFieldDisabled(LoadIsSingleItemField.fieldName);
    if (cannotChangeToMultipleItems) {
      return true;
    }

    return isFieldDisabled(LoadDispositionField.fieldName);
  }, [loadIsSingleItemValue, isFieldDisabled]);

  useEffect(() => {
    if (loadIsSingleItemValue === undefined) {
      setValue(LoadIsSingleItemField.fieldName, isSingleItemValue);
    }
  }, [loadIsSingleItemValue, setValue, isSingleItemValue]);

  useEffect(() => {
    if (isSingleItemValue) {
      const fields = [
        LoadDispositionField.fieldName,
        LoadSideBySideCountField.fieldName,
        LoadEndToEndCountField.fieldName,
        LoadStackCountField.fieldName,
      ];

      fields.forEach((fieldName) => {
        unregister(fieldName);
      });
    }
  }, [isSingleItemValue, unregister]);

  if (props.fields.length <= 0) {
    return null;
  }

  return (
    <EditOrderCollapsible
      title={t('loadInformation')}
      id="load-information-section"
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
      inError={sectionInError}
    >
      <Stack gap={theme.spacing['3']}>
        <ConditionalField
          fields={props.fields}
          conditions={LoadDescriptionField.fieldName}
        >
          <FormTextInput
            multiline
            register={register}
            errors={errors}
            disabled={
              formDisabled || isFieldDisabled(LoadDescriptionField.fieldName)
            }
            fieldName={LoadDescriptionField.fieldName}
          />
        </ConditionalField>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={FloridaPermitTypeField.fieldName}
          >
            <FormDropdown
              fieldName={FloridaPermitTypeField.fieldName}
              disabled={
                formDisabled ||
                isFieldDisabled(FloridaPermitTypeField.fieldName)
              }
              options={FloridaPermitTypeField.options.map((option) => ({
                label: t(`fieldOptions.${option}`),
                value: option,
              }))}
              control={control}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={FloridaLoadDescriptionField.fieldName}
          >
            <FormDropdown
              fieldName={FloridaLoadDescriptionField.fieldName}
              disabled={
                formDisabled ||
                isFieldDisabled(FloridaLoadDescriptionField.fieldName)
              }
              options={FloridaLoadDescriptionField.options.map((option) => ({
                label: t(`fieldOptions.${option}`),
                value: option,
              }))}
              control={control}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={FloridaLoadIdentityField.fieldName}
          >
            <FormDropdown
              fieldName={FloridaLoadIdentityField.fieldName}
              disabled={
                formDisabled ||
                isFieldDisabled(FloridaLoadIdentityField.fieldName)
              }
              options={FloridaLoadIdentityField.options.map((option) => ({
                label: t(`fieldOptions.${option}`),
                value: option,
              }))}
              control={control}
            />
          </ConditionalField>
        </Stack>
        <ConditionalField
          fields={props.fields}
          conditions={CommodityField.fieldName}
        >
          <FormTextInput
            register={register}
            errors={errors}
            disabled={formDisabled || isFieldDisabled(CommodityField.fieldName)}
            fieldName={CommodityField.fieldName}
            multiline
          />
        </ConditionalField>

        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={LoadMakeField.fieldName}
          >
            <FormTextInput
              register={register}
              errors={errors}
              disabled={
                formDisabled || isFieldDisabled(LoadMakeField.fieldName)
              }
              fieldName={LoadMakeField.fieldName}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={LoadModelField.fieldName}
          >
            <FormTextInput
              register={register}
              errors={errors}
              disabled={
                formDisabled || isFieldDisabled(LoadModelField.fieldName)
              }
              fieldName={LoadModelField.fieldName}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={LoadSerialNumberField.fieldName}
          >
            <FormTextInput
              register={register}
              errors={errors}
              disabled={
                formDisabled || isFieldDisabled(LoadSerialNumberField.fieldName)
              }
              fieldName={LoadSerialNumberField.fieldName}
            />
          </ConditionalField>
        </Stack>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={LoadIndustryTypeField.fieldName}
          >
            <FormDropdown
              fieldName={LoadIndustryTypeField.fieldName}
              disabled={
                formDisabled || isFieldDisabled(LoadIndustryTypeField.fieldName)
              }
              options={LoadIndustryTypeField.options.map((option) => ({
                label: t(`fieldOptions.${option}`),
                value: option,
              }))}
              control={control}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={LoadQuantityField.fieldName}
          >
            <FormNumberInput
              register={register}
              errors={errors}
              disabled={
                formDisabled || isFieldDisabled(LoadQuantityField.fieldName)
              }
              fieldName={LoadQuantityField.fieldName}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={LoadYearField.fieldName}
          >
            <FormNumberInput
              register={register}
              errors={errors}
              disabled={
                formDisabled || isFieldDisabled(LoadYearField.fieldName)
              }
              fieldName={LoadYearField.fieldName}
            />
          </ConditionalField>
        </Stack>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={LoadLoadingMethod.fieldName}
          >
            <FormDropdown
              fieldName={LoadLoadingMethod.fieldName}
              disabled={
                formDisabled || isFieldDisabled(LoadLoadingMethod.fieldName)
              }
              options={LoadLoadingMethod.options.map((option) => ({
                value: option,
                label: t(`fieldOptions.${option}`),
              }))}
              control={control}
            />
          </ConditionalField>
        </Stack>

        <ConditionalField
          fields={props.fields}
          conditions={LoadIsSingleItemField.fieldName}
        >
          <ControllerFormCheckbox
            control={control}
            disabled={formDisabled || isLoadIsSingleItemDisabled}
            fieldName={LoadIsSingleItemField.fieldName}
          />
        </ConditionalField>

        {!isSingleItemValue && (
          <Stack gap={theme.spacing['3']}>
            <ConditionalField
              fields={props.fields}
              conditions={LoadDispositionField.fieldName}
            >
              <FormMultipleOptions
                control={control}
                disabled={formDisabled || isLoadDispositionDisabled}
                fieldName={LoadDispositionField.fieldName}
                options={LoadDispositionField.options.map((option) => ({
                  value: option,
                  label: t(`fieldOptions.${option}`),
                }))}
              />
            </ConditionalField>

            <Stack direction="row" gap={theme.spacing['3']}>
              {hasLoadDisposition(
                loadDispositionValue,
                LoadDisposition.loadIsSideBySide,
              ) && (
                <ConditionalField
                  fields={props.fields}
                  conditions={LoadSideBySideCountField.fieldName}
                >
                  <FormNumberInput
                    register={register}
                    errors={errors}
                    disabled={
                      formDisabled ||
                      isFieldDisabled(LoadSideBySideCountField.fieldName)
                    }
                    fieldName={LoadSideBySideCountField.fieldName}
                  />
                </ConditionalField>
              )}
              {hasLoadDisposition(
                loadDispositionValue,
                LoadDisposition.loadIsEndToEnd,
              ) && (
                <ConditionalField
                  fields={props.fields}
                  conditions={LoadEndToEndCountField.fieldName}
                >
                  <FormNumberInput
                    register={register}
                    errors={errors}
                    disabled={
                      formDisabled ||
                      isFieldDisabled(LoadEndToEndCountField.fieldName)
                    }
                    fieldName={LoadEndToEndCountField.fieldName}
                  />
                </ConditionalField>
              )}
              {hasLoadDisposition(
                loadDispositionValue,
                LoadDisposition.loadIsStacked,
              ) && (
                <ConditionalField
                  fields={props.fields}
                  conditions={LoadStackCountField.fieldName}
                >
                  <FormNumberInput
                    register={register}
                    errors={errors}
                    disabled={
                      formDisabled ||
                      isFieldDisabled(LoadStackCountField.fieldName)
                    }
                    fieldName={LoadStackCountField.fieldName}
                  />
                </ConditionalField>
              )}
            </Stack>
          </Stack>
        )}

        <ConditionalField
          fields={props.fields}
          conditions={LoadIsNewlyManufacturedField.fieldName}
        >
          <ControllerFormCheckbox
            control={control}
            disabled={
              formDisabled ||
              isFieldDisabled(LoadIsNewlyManufacturedField.fieldName)
            }
            fieldName={LoadIsNewlyManufacturedField.fieldName}
          />
        </ConditionalField>

        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={LoadOwnerNameField.fieldName}
          >
            <FormTextInput
              register={register}
              errors={errors}
              disabled={
                formDisabled || isFieldDisabled(LoadOwnerNameField.fieldName)
              }
              fieldName={LoadOwnerNameField.fieldName}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={LoadOwnerAddressField.fieldName}
          >
            <FormTextInput
              register={register}
              errors={errors}
              disabled={
                formDisabled || isFieldDisabled(LoadOwnerAddressField.fieldName)
              }
              fieldName={LoadOwnerAddressField.fieldName}
            />
          </ConditionalField>
        </Stack>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={LoadLengthField.fieldName}
          >
            <FormDimensionInput
              control={control}
              disabled={
                formDisabled || isFieldDisabled(LoadLengthField.fieldName)
              }
              fieldName={LoadLengthField.fieldName}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={LoadWidthField.fieldName}
          >
            <FormDimensionInput
              control={control}
              disabled={
                formDisabled || isFieldDisabled(LoadWidthField.fieldName)
              }
              fieldName={LoadWidthField.fieldName}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={LoadHeightField.fieldName}
          >
            <FormDimensionInput
              control={control}
              disabled={
                formDisabled || isFieldDisabled(LoadHeightField.fieldName)
              }
              fieldName={LoadHeightField.fieldName}
            />
          </ConditionalField>
        </Stack>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={LoadWeightField.fieldName}
          >
            <FormWeightInput
              control={control}
              disabled={
                formDisabled || isFieldDisabled(LoadWeightField.fieldName)
              }
              fieldName={LoadWeightField.fieldName}
              multiplyByThousand={true}
            />
          </ConditionalField>
        </Stack>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={LoadSealedContainerNumberField.fieldName}
          >
            <FormTextInput
              register={register}
              errors={errors}
              disabled={
                formDisabled ||
                isFieldDisabled(LoadSealedContainerNumberField.fieldName)
              }
              fieldName={LoadSealedContainerNumberField.fieldName}
            />
          </ConditionalField>

          <ConditionalField
            fields={props.fields}
            conditions={LoadSealedContainerChassisNumberField.fieldName}
          >
            <FormTextInput
              register={register}
              errors={errors}
              disabled={
                formDisabled ||
                isFieldDisabled(LoadSealedContainerChassisNumberField.fieldName)
              }
              fieldName={LoadSealedContainerChassisNumberField.fieldName}
            />
          </ConditionalField>
        </Stack>
      </Stack>
    </EditOrderCollapsible>
  );
};

const hasLoadDisposition = (
  selectValue: BaseInputType,
  loadDisposition: string,
): boolean => {
  return !!selectValue && (selectValue as string[]).includes(loadDisposition);
};
